// Block Jam - pure game logic (no DOM). Also runs under Node for level validation.
//
// Rules: colored blocks sit on a grid, each locked to one axis (h or v). A block
// slides along its track until it hits a wall cell, another block, or the border.
// The board edges have colored gates; when a block slides flush against a gate of
// its own color it funnels out and is removed. Clear every block to win.
//
// Special elements:
//  - walls: static cells nothing can pass through;
//  - ice blocks (melt=N): frozen until N blocks have exited the board;
//  - rainbow blocks: may exit through ANY gate.

export type Color =
  | "red"
  | "blue"
  | "green"
  | "yellow"
  | "purple"
  | "orange"
  | "cyan"
  | "pink"
  | "rainbow";
export type Orient = "h" | "v";
export type Side = "top" | "bottom" | "left" | "right";
export type Dir = "up" | "down" | "left" | "right";

export interface Block {
  id: number;
  color: Color;
  r: number; // top row
  c: number; // left col
  len: number; // length along orientation (1 = single cell)
  o: Orient;
  /** Ice: frozen until this many blocks have exited the board. */
  melt?: number;
}

export interface Gate {
  side: Side;
  index: number; // column for top/bottom, row for left/right
  color: Color;
}

export interface Level {
  w: number;
  h: number;
  blocks: Block[];
  gates: Gate[];
  /** Static obstacle cells as [row, col]. */
  walls?: Array<[number, number]>;
  par?: number; // optimal move count (filled by the level generator/validator)
}

export interface Move {
  blockId: number;
  dir: Dir;
  type: "slide" | "exit";
  to?: { r: number; c: number };
}

export type State = Block[]; // alive blocks only

const DIRS: Record<Dir, [number, number]> = {
  up: [-1, 0],
  down: [1, 0],
  left: [0, -1],
  right: [0, 1],
};

const SIDE_OF_DIR: Record<Dir, Side> = {
  up: "top",
  down: "bottom",
  left: "left",
  right: "right",
};

export function blockCells(b: Block): Array<[number, number]> {
  const cells: Array<[number, number]> = [];
  for (let i = 0; i < b.len; i++) {
    cells.push(b.o === "h" ? [b.r, b.c + i] : [b.r + i, b.c]);
  }
  return cells;
}

// Every block slides on a single track (shown with an arrow): horizontal blocks
// move left/right, vertical blocks up/down - including 1x1 blocks, which carry an
// orientation too. This makes the puzzle a proper Rush Hour-style sliding puzzle.
export function allowedDirs(b: Block): Dir[] {
  return b.o === "h" ? ["left", "right"] : ["up", "down"];
}

function cellKey(r: number, c: number): number {
  return r * 1000 + c;
}

// Wall cells per level, cached (levels are immutable once loaded).
const wallCache = new WeakMap<Level, Set<number>>();
export function wallSet(level: Level): Set<number> {
  let set = wallCache.get(level);
  if (!set) {
    set = new Set((level.walls ?? []).map(([r, c]) => cellKey(r, c)));
    wallCache.set(level, set);
  }
  return set;
}

/** How many blocks have already exited the board in this state. */
export function exitedCount(level: Level, state: State): number {
  return level.blocks.length - state.length;
}

/** Ice blocks stay frozen until enough other blocks have exited. */
export function isFrozen(level: Level, state: State, b: Block): boolean {
  return (b.melt ?? 0) > exitedCount(level, state);
}

function occupancyExcept(level: Level, state: State, exceptId: number): Set<number> {
  const set = new Set<number>(wallSet(level));
  for (const b of state) {
    if (b.id === exceptId) continue;
    for (const [r, c] of blockCells(b)) set.add(cellKey(r, c));
  }
  return set;
}

function findGate(level: Level, side: Side, index: number): Gate | undefined {
  return level.gates.find((g) => g.side === side && g.index === index);
}

/** Maximum number of cells `b` can shift in direction `d` without leaving the board or overlapping. */
function maxSlide(level: Level, occ: Set<number>, b: Block, d: Dir): number {
  const [dr, dc] = DIRS[d];
  const cells = blockCells(b);
  let s = 0;
  for (;;) {
    const ns = s + 1;
    let ok = true;
    for (const [r, c] of cells) {
      const nr = r + dr * ns;
      const nc = c + dc * ns;
      if (nr < 0 || nr >= level.h || nc < 0 || nc >= level.w || occ.has(cellKey(nr, nc))) {
        ok = false;
        break;
      }
    }
    if (!ok) break;
    s = ns;
  }
  return s;
}

/** Is the block (after sliding) flush against the board edge on side `d`, and what is the leading perpendicular coord? */
function leadingInfo(level: Level, b: Block, d: Dir): { flush: boolean; perp: number } {
  const cells = blockCells(b);
  if (d === "right") {
    const maxCol = Math.max(...cells.map((c) => c[1]));
    return { flush: maxCol === level.w - 1, perp: b.r };
  }
  if (d === "left") {
    const minCol = Math.min(...cells.map((c) => c[1]));
    return { flush: minCol === 0, perp: b.r };
  }
  if (d === "down") {
    const maxRow = Math.max(...cells.map((c) => c[0]));
    return { flush: maxRow === level.h - 1, perp: b.c };
  }
  const minRow = Math.min(...cells.map((c) => c[0]));
  return { flush: minRow === 0, perp: b.c };
}

/** Does this gate let this block out? Rainbow blocks exit through any gate. */
function gateMatches(gate: Gate | undefined, b: Block): boolean {
  return Boolean(gate && (b.color === "rainbow" || gate.color === b.color));
}

/** Generate the legal moves for a block in a state. */
export function movesForBlock(level: Level, state: State, b: Block): Move[] {
  if (isFrozen(level, state, b)) return [];
  const occ = occupancyExcept(level, state, b.id);
  const out: Move[] = [];
  for (const d of allowedDirs(b)) {
    const s = maxSlide(level, occ, b, d);
    const [dr, dc] = DIRS[d];
    const slid: Block = { ...b, r: b.r + dr * s, c: b.c + dc * s };
    const { flush, perp } = leadingInfo(level, slid, d);
    const gate = flush ? findGate(level, SIDE_OF_DIR[d], perp) : undefined;
    if (gateMatches(gate, b)) {
      out.push({ blockId: b.id, dir: d, type: "exit" });
    } else if (s >= 1) {
      out.push({ blockId: b.id, dir: d, type: "slide", to: { r: slid.r, c: slid.c } });
    }
  }
  return out;
}

/** How far a block can slide in a direction, and whether reaching the edge exits it. Used by the drag UI. */
export function slideInfo(level: Level, state: State, b: Block, d: Dir): { steps: number; exit: boolean } {
  if (isFrozen(level, state, b)) return { steps: 0, exit: false };
  const occ = occupancyExcept(level, state, b.id);
  const s = maxSlide(level, occ, b, d);
  const [dr, dc] = DIRS[d];
  const slid: Block = { ...b, r: b.r + dr * s, c: b.c + dc * s };
  const { flush, perp } = leadingInfo(level, slid, d);
  const gate = flush ? findGate(level, SIDE_OF_DIR[d], perp) : undefined;
  return { steps: s, exit: gateMatches(gate, b) };
}

export function legalMoves(level: Level, state: State): Move[] {
  const out: Move[] = [];
  for (const b of state) out.push(...movesForBlock(level, state, b));
  return out;
}

/**
 * Complete move set for SOLVERS: every partial slide position (1..maxSlide) plus
 * exits. Players can stop a block anywhere, so a correct solver must consider all
 * intermediate positions, not just the maximal slide that movesForBlock returns.
 */
export function solverMoves(level: Level, state: State): Move[] {
  const out: Move[] = [];
  for (const b of state) {
    for (const d of allowedDirs(b)) {
      const info = slideInfo(level, state, b, d);
      const [dr, dc] = DIRS[d];
      for (let k = 1; k <= info.steps; k++) {
        out.push({ blockId: b.id, dir: d, type: "slide", to: { r: b.r + dr * k, c: b.c + dc * k } });
      }
      if (info.exit) out.push({ blockId: b.id, dir: d, type: "exit" });
    }
  }
  return out;
}

export function applyMove(state: State, move: Move): State {
  if (move.type === "exit") {
    return state.filter((b) => b.id !== move.blockId);
  }
  return state.map((b) => (b.id === move.blockId && move.to ? { ...b, r: move.to.r, c: move.to.c } : b));
}

export function isSolved(state: State): boolean {
  return state.length === 0;
}

export function cloneLevelState(level: Level): State {
  return level.blocks.map((b) => ({ ...b }));
}

export function stateKey(state: State): string {
  return state
    .map((b) => `${b.id}@${b.r},${b.c}`)
    .sort()
    .join("|");
}

/** Is this move legal in the given state? Handles partial slides (any 1..maxSlide). */
export function moveLegal(level: Level, state: State, mv: Move): boolean {
  const b = state.find((x) => x.id === mv.blockId);
  if (!b) return false;
  if (mv.type === "exit") return slideInfo(level, state, b, mv.dir).exit;
  if (!mv.to) return false;
  const [dr, dc] = DIRS[mv.dir];
  const steps = Math.abs(mv.to.r - b.r) + Math.abs(mv.to.c - b.c);
  if (steps < 1) return false;
  if (b.r + dr * steps !== mv.to.r || b.c + dc * steps !== mv.to.c) return false;
  return steps <= slideInfo(level, state, b, mv.dir).steps;
}

/** Replay a recorded solution from the start state; true if every move is legal and the board clears. */
export function replaySolution(level: Level, solution: Move[]): boolean {
  let state = cloneLevelState(level);
  for (const mv of solution) {
    if (!moveLegal(level, state, mv)) return false;
    state = applyMove(state, mv);
  }
  return isSolved(state);
}

/**
 * Suggest the next block to move (for the hint button). Prefers a block that can
 * exit now; else a slide that unblocks an exit (1-ply); else a bounded search.
 */
export function hintMove(level: Level, state: State): number | null {
  for (const b of state) {
    if (movesForBlock(level, state, b).some((m) => m.type === "exit")) return b.id;
  }
  for (const b of state) {
    for (const mv of movesForBlock(level, state, b)) {
      if (mv.type !== "slide") continue;
      const ns = applyMove(state, mv);
      if (ns.some((x) => movesForBlock(level, ns, x).some((m) => m.type === "exit"))) return b.id;
    }
  }
  const path = solvePathFrom(level, state, 50000);
  return path && path.length ? path[0].blockId : null;
}

/** Shortest sequence of slides from `start` that makes some block exitable. */
function findUnlock(level: Level, start: State, maxDepth: number, cap: number): Move[] | null {
  if (legalMoves(level, start).some((m) => m.type === "exit")) return [];
  const startKey = stateKey(start);
  const seen = new Set<string>([startKey]);
  const parent = new Map<string, { key: string; move: Move }>();
  const states = new Map<string, State>([[startKey, start]]);
  let frontier = [startKey];
  let depth = 0;
  while (frontier.length && seen.size < cap && depth < maxDepth) {
    depth++;
    const next: string[] = [];
    for (const key of frontier) {
      const st = states.get(key)!;
      for (const mv of solverMoves(level, st)) {
        if (mv.type !== "slide") continue;
        const ns = applyMove(st, mv);
        const nk = stateKey(ns);
        if (seen.has(nk)) continue;
        seen.add(nk);
        parent.set(nk, { key, move: mv });
        states.set(nk, ns);
        if (legalMoves(level, ns).some((m) => m.type === "exit")) {
          const seq: Move[] = [];
          let cur = nk;
          while (cur !== startKey) {
            const p = parent.get(cur)!;
            seq.unshift(p.move);
            cur = p.key;
          }
          return seq;
        }
        next.push(nk);
      }
    }
    frontier = next;
  }
  return null;
}

export interface SolveStats {
  /** Total moves of the realistic solve (used as par). */
  moves: number;
  /**
   * How many times the solver was fully stuck (no exit available) and had to
   * plan a slide sequence to unlock the next exit. This counts the genuine
   * "figure out the order" moments a player faces, so it is the generator's
   * primary measure of ordering depth.
   */
  unlockPhases: number;
}

/**
 * A realistic "good player" solve used for the par / star rating and for
 * difficulty measurement: repeatedly exit whatever can exit, otherwise make the
 * shortest slide sequence that unlocks the next exit. Returns null if it gets
 * stuck (caller then falls back to the constructed solution length).
 */
export function realisticSolveStats(level: Level): SolveStats | null {
  let state = cloneLevelState(level);
  let moves = 0;
  let unlockPhases = 0;
  let guard = 0;
  while (state.length > 0 && guard++ < 10000) {
    const exit = legalMoves(level, state).find((m) => m.type === "exit");
    if (exit) {
      state = applyMove(state, exit);
      moves++;
      continue;
    }
    const seq = findUnlock(level, state, 8, 60000);
    if (!seq || seq.length === 0) return null;
    unlockPhases++;
    for (const mv of seq) {
      state = applyMove(state, mv);
      moves++;
    }
  }
  return state.length === 0 ? { moves, unlockPhases } : null;
}

/** Realistic solve length only (see realisticSolveStats). */
export function realisticSolve(level: Level): number | null {
  return realisticSolveStats(level)?.moves ?? null;
}

/** Breadth-first search for the shortest solution length. Returns null if not solved within the cap. */
export function solve(level: Level, maxStates = 400000): number | null {
  const start = cloneLevelState(level);
  if (isSolved(start)) return 0;
  const seen = new Set<string>([stateKey(start)]);
  let frontier: State[] = [start];
  let depth = 0;
  while (frontier.length && seen.size < maxStates) {
    depth++;
    const next: State[] = [];
    for (const st of frontier) {
      for (const mv of legalMoves(level, st)) {
        const ns = applyMove(st, mv);
        if (isSolved(ns)) return depth;
        const key = stateKey(ns);
        if (!seen.has(key)) {
          seen.add(key);
          next.push(ns);
        }
      }
    }
    frontier = next;
  }
  return null;
}

export function solvePath(level: Level, maxStates = 400000): Move[] | null {
  return solvePathFrom(level, cloneLevelState(level), maxStates);
}

/** BFS that reconstructs one shortest solution path from an arbitrary start state. */
export function solvePathFrom(level: Level, start: State, maxStates = 400000): Move[] | null {
  if (isSolved(start)) return [];
  const startKey = stateKey(start);
  const seen = new Set<string>([startKey]);
  const parent = new Map<string, { key: string; move: Move }>();
  const states = new Map<string, State>([[startKey, start]]);
  let frontier = [startKey];

  while (frontier.length && seen.size < maxStates) {
    const next: string[] = [];
    for (const key of frontier) {
      const st = states.get(key)!;
      for (const mv of legalMoves(level, st)) {
        const ns = applyMove(st, mv);
        const nk = stateKey(ns);
        if (isSolved(ns)) {
          const path = [mv];
          let cur = key;
          while (cur !== startKey) {
            const p = parent.get(cur)!;
            path.unshift(p.move);
            cur = p.key;
          }
          return path;
        }
        if (!seen.has(nk)) {
          seen.add(nk);
          parent.set(nk, { key, move: mv });
          states.set(nk, ns);
          next.push(nk);
        }
      }
    }
    frontier = next;
  }
  return null;
}
